import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatXOF, API, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { toast } from "sonner";
import {
  Save, Mail, Phone, MapPin, MessageCircle, Type, Wallet, ChevronLeft,
  Smartphone, Eye, EyeOff, Trash2, Plus, X, Image as ImageIcon, FileText, Download, History, Clock,
  User as UserIcon, Key, Lock,
} from "lucide-react";
import ImageUploader from "@/components/jamm/ImageUploader";

function Field({ icon: Icon, label, hint, ...props }) {
  return (
    <div>
      <label className="block text-sm text-ink-soft mb-1.5">{label}</label>
      <div className="flex items-center rounded-xl border border-border bg-white px-3 focus-within:ring-2 focus-within:ring-sage-300">
        {Icon && <Icon className="w-4 h-4 text-ink-mute" strokeWidth={1.5} />}
        <input {...props} className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none" />
      </div>
      {hint && <p className="text-xs text-ink-mute mt-1">{hint}</p>}
    </div>
  );
}

function Toggle({ value, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!value)} className={`w-11 h-6 rounded-full transition shrink-0 ${value ? "bg-sage-500" : "bg-border"}`} data-testid={testid}>
      <span className={`block w-5 h-5 rounded-full bg-white transition shadow translate-y-0.5 ${value ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

function PwdInput({ value, onChange, show, onToggle, placeholder, testid, autoComplete }) {
  return (
    <div className="flex items-center rounded-xl border border-border bg-white px-3 focus-within:ring-2 focus-within:ring-sage-300">
      <Lock className="w-4 h-4 text-ink-mute" />
      <input
        required
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none"
        data-testid={testid}
      />
      <button type="button" onClick={onToggle} className="p-2 -mr-2 text-ink-mute hover:text-ink-soft rounded-lg" tabIndex={-1} data-testid={`${testid}-toggle`}>
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

function MyAccountCard() {
  const { user, refresh } = useAuth();
  // Email change
  const [emailForm, setEmailForm] = useState({ current_password: "", new_email: "" });
  const [showEmailPwd, setShowEmailPwd] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  // Password change
  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.new_email || !emailForm.current_password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    setSavingEmail(true);
    try {
      await api.put("/users/me/email", emailForm);
      toast.success("Email mis à jour. Reconnectez-vous avec le nouvel email.");
      setEmailForm({ current_password: "", new_email: "" });
      if (refresh) await refresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Impossible de modifier l'email");
    } finally {
      setSavingEmail(false);
    }
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    if (pwdForm.new_password.length < 6) {
      toast.error("Nouveau mot de passe : 6 caractères minimum");
      return;
    }
    if (pwdForm.new_password !== pwdForm.confirm) {
      toast.error("Les deux nouveaux mots de passe ne correspondent pas");
      return;
    }
    setSavingPwd(true);
    try {
      await api.put("/users/me/password", {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      });
      toast.success("Mot de passe mis à jour");
      setPwdForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Impossible de modifier le mot de passe");
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="card-soft lg:col-span-2 bg-gradient-to-br from-sage-50/80 via-bone to-sage-50/40" data-testid="my-account-card">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="grid place-items-center w-12 h-12 rounded-2xl bg-sage-500 text-white shrink-0">
          <UserIcon className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-2xl text-ink">Mon compte</h2>
          <p className="text-ink-soft text-sm mt-1">
            Connecté·e en tant que <strong className="text-ink" data-testid="my-account-current-email">{user?.email || "—"}</strong>
            {user?.name && <span> · {user.name}</span>}
          </p>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-6">
        {/* Email change */}
        <form onSubmit={submitEmail} className="rounded-2xl bg-white border border-border p-5" data-testid="my-account-email-form">
          <div className="overline text-sage-600 mb-3">Changer mon email</div>
          <label className="block text-sm text-ink-soft mb-1.5">Nouvel email</label>
          <div className="flex items-center rounded-xl border border-border bg-white px-3 focus-within:ring-2 focus-within:ring-sage-300">
            <Mail className="w-4 h-4 text-ink-mute" />
            <input
              required type="email"
              value={emailForm.new_email}
              onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
              placeholder="nouveau@email.com"
              autoComplete="off"
              className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none"
              data-testid="my-account-new-email"
            />
          </div>
          <label className="block mt-3 text-sm text-ink-soft mb-1.5">Mot de passe actuel <span className="text-ink-mute text-xs">(pour confirmer)</span></label>
          <PwdInput
            value={emailForm.current_password}
            onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
            show={showEmailPwd}
            onToggle={() => setShowEmailPwd((v) => !v)}
            placeholder="••••••••"
            autoComplete="current-password"
            testid="my-account-email-current-pwd"
          />
          <button type="submit" disabled={savingEmail} className="btn-primary mt-5 w-full justify-center" data-testid="my-account-save-email">
            <Save className="w-4 h-4" /> {savingEmail ? "Enregistrement…" : "Enregistrer le nouvel email"}
          </button>
          <p className="text-xs text-ink-mute mt-2 text-center">Vous devrez vous reconnecter avec le nouvel email.</p>
        </form>

        {/* Password change */}
        <form onSubmit={submitPwd} className="rounded-2xl bg-white border border-border p-5" data-testid="my-account-password-form">
          <div className="overline text-gold mb-3">Changer mon mot de passe</div>
          <label className="block text-sm text-ink-soft mb-1.5">Mot de passe actuel</label>
          <PwdInput
            value={pwdForm.current_password}
            onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
            show={showCur}
            onToggle={() => setShowCur((v) => !v)}
            placeholder="••••••••"
            autoComplete="current-password"
            testid="my-account-pwd-current"
          />
          <label className="block mt-3 text-sm text-ink-soft mb-1.5">Nouveau mot de passe <span className="text-ink-mute text-xs">(6+ caractères)</span></label>
          <PwdInput
            value={pwdForm.new_password}
            onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
            show={showNew}
            onToggle={() => setShowNew((v) => !v)}
            placeholder="••••••••"
            autoComplete="new-password"
            testid="my-account-pwd-new"
          />
          <label className="block mt-3 text-sm text-ink-soft mb-1.5">Confirmer le nouveau mot de passe</label>
          <PwdInput
            value={pwdForm.confirm}
            onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
            show={showNew}
            onToggle={() => setShowNew((v) => !v)}
            placeholder="••••••••"
            autoComplete="new-password"
            testid="my-account-pwd-confirm"
          />
          <button type="submit" disabled={savingPwd} className="btn-primary mt-5 w-full justify-center" data-testid="my-account-save-password">
            <Key className="w-4 h-4" /> {savingPwd ? "Enregistrement…" : "Mettre à jour le mot de passe"}
          </button>
        </form>
      </div>
    </div>
  );
}

const SPEC_OPTIONS = ["Écoute émotionnelle", "Soutien femmes", "Immigration", "Famille", "Jeunes", "Couples", "Motivation", "Orientation"];
const LANG_OPTIONS = ["Français", "Wolof", "Anglais"];
const DAYS = ["lun", "mar", "mer", "jeu", "ven", "sam", "dim"];

function AccompagnateurEditor({ acc, onSaved, onClose }) {
  const [form, setForm] = useState({ ...acc });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleArr = (k, val) => {
    const cur = form[k] || [];
    const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
    update(k, next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, title: form.title, bio: form.bio,
        rate_15: Number(form.rate_15) || 0,
        rate_30: Number(form.rate_30) || 0,
        rate_60: Number(form.rate_60) || 0,
        specialties: form.specialties || [],
        languages: form.languages || [],
        country: form.country,
        avatar_url: form.avatar_url,
        available_days: form.available_days || [],
        active: form.active !== false,
      };
      const { data } = await api.put(`/admin/accompagnateurs/${acc.id}`, payload);
      toast.success("Mis à jour");
      onSaved(data.accompagnateur);
    } catch (e) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="acc-editor-modal">
      <div className="bg-bone rounded-3xl max-w-2xl w-full my-12 p-6 md:p-8 shadow-2xl relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-beige" data-testid="acc-editor-close"><X className="w-5 h-5" /></button>
        <div className="flex items-center gap-3">
          <img src={form.avatar_url} alt="" className="w-14 h-14 rounded-full object-cover ring-2 ring-sage-100" />
          <div>
            <div className="overline">Éditer</div>
            <h2 className="font-display text-2xl text-ink">{acc.name}</h2>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <Field label="Nom" value={form.name || ""} onChange={(e) => update("name", e.target.value)} data-testid="acc-edit-name" />
          <Field label="Titre" value={form.title || ""} onChange={(e) => update("title", e.target.value)} data-testid="acc-edit-title" />
        </div>

        <div className="mt-4">
          <label className="block text-sm text-ink-soft mb-1.5">Bio</label>
          <textarea rows={4} value={form.bio || ""} onChange={(e) => update("bio", e.target.value)} className="w-full rounded-xl border border-border bg-white px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-sage-300" data-testid="acc-edit-bio" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <Field label="Pays" value={form.country || ""} onChange={(e) => update("country", e.target.value)} data-testid="acc-edit-country" />
          <div>
            <ImageUploader label="Photo de profil" aspect="1/1" value={form.avatar_url} onChange={(url) => update("avatar_url", url)} testid="acc-edit-avatar-uploader" />
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          <Field label="Tarif 15 min" type="number" value={form.rate_15 ?? 0} onChange={(e) => update("rate_15", e.target.value)} hint={formatXOF(Number(form.rate_15) || 0)} data-testid="acc-edit-r15" />
          <Field label="Tarif 30 min" type="number" value={form.rate_30 ?? 0} onChange={(e) => update("rate_30", e.target.value)} hint={formatXOF(Number(form.rate_30) || 0)} data-testid="acc-edit-r30" />
          <Field label="Tarif 60 min" type="number" value={form.rate_60 ?? 0} onChange={(e) => update("rate_60", e.target.value)} hint={formatXOF(Number(form.rate_60) || 0)} data-testid="acc-edit-r60" />
        </div>

        <div className="mt-5">
          <div className="overline mb-2">Spécialités</div>
          <div className="flex flex-wrap gap-2">
            {SPEC_OPTIONS.map((s) => {
              const on = (form.specialties || []).includes(s);
              return (
                <button key={s} type="button" onClick={() => toggleArr("specialties", s)} className={`text-xs px-3 py-1.5 rounded-full border transition ${on ? "bg-sage-50 border-sage-300 text-sage-700" : "bg-white border-border text-ink-soft hover:border-sage-300"}`}>
                  {s}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-5">
          <div className="overline mb-2">Langues</div>
          <div className="flex flex-wrap gap-2">
            {LANG_OPTIONS.map((s) => {
              const on = (form.languages || []).includes(s);
              return (
                <button key={s} type="button" onClick={() => toggleArr("languages", s)} className={`text-xs px-3 py-1.5 rounded-full border transition ${on ? "bg-gold-light/40 border-gold text-ink" : "bg-white border-border text-ink-soft hover:border-sage-300"}`}>
                  {s}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-5">
          <div className="overline mb-2">Jours disponibles</div>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d) => {
              const on = (form.available_days || []).includes(d);
              return (
                <button key={d} type="button" onClick={() => toggleArr("available_days", d)} className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border transition ${on ? "bg-sage-500 border-sage-500 text-white" : "bg-white border-border text-ink-soft hover:border-sage-300"}`}>
                  {d}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between rounded-xl bg-beige/60 border border-border p-3">
          <div>
            <div className="text-sm font-medium text-ink">Actif</div>
            <div className="text-xs text-ink-mute">Visible dans la liste publique des accompagnateurs.</div>
          </div>
          <Toggle value={form.active !== false} onChange={(v) => update("active", v)} testid="acc-edit-active" />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="btn-outline" data-testid="acc-edit-cancel">Annuler</button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="acc-edit-save">
            <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const { refresh: refreshSettings } = useSettings();
  const [loading, setLoading] = useState(true);
  const [savingSite, setSavingSite] = useState(false);
  const [form, setForm] = useState({
    app_name: "", contact_email: "", contact_phone: "", contact_location: "",
    whatsapp_number: "", plan_free_price: 0, plan_premium_price: 0, plan_vip_price: 0,
    wave_number: "", wave_recipient: "", wave_enabled: true,
    accompagnateurs_visible: false, tarifs_visible: false, primary_accompagnateur_id: "",
    logo_url: "", hero_image_url: "", how_image_url: "",
    tarif_price_afrique: 5500, tarif_price_europe: 15, tarif_price_usd: 18, tarif_price_cad: 24,
    promo_active: true, promo_amount: "",
  });

  const [accs, setAccs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const [docVersions, setDocVersions] = useState([]);
  const [creatingDoc, setCreatingDoc] = useState(false);
  const [docNote, setDocNote] = useState("");

  const loadDocVersions = async () => {
    try {
      const { data } = await api.get("/admin/documentation/versions");
      setDocVersions(data.items || []);
    } catch (e) {
      // silent
    }
  };

  const createDocVersion = async () => {
    setCreatingDoc(true);
    try {
      await api.post("/admin/documentation/versions", { note: docNote });
      toast.success("Nouvelle version archivée");
      setDocNote("");
      loadDocVersions();
    } catch (e) {
      toast.error("Impossible d'archiver la version");
    } finally {
      setCreatingDoc(false);
    }
  };

  const downloadDocVersion = async (v) => {
    try {
      const res = await fetch(`${API}/admin/documentation/versions/${v.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = v.filename || `documentation-${v.label}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Téléchargement impossible");
    }
  };

  const deleteDocVersion = async (v) => {
    if (!confirm(`Supprimer la version ${v.label} ? Cette action est définitive.`)) return;
    try {
      await api.delete(`/admin/documentation/versions/${v.id}`);
      toast.success(`Version ${v.label} supprimée`);
      loadDocVersions();
    } catch (e) {
      toast.error("Suppression impossible");
    }
  };

  const loadAll = async () => {
    try {
      const [s, a] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/admin/accompagnateurs"),
      ]);
      setForm((f) => ({ ...f, ...s.data.settings }));
      setAccs(a.data.items || []);
    } catch (e) {
      toast.error("Impossible de charger les paramètres");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); loadDocVersions(); }, []);

  const saveSite = async (e) => {
    e?.preventDefault?.();
    setSavingSite(true);
    try {
      const payload = {
        app_name: form.app_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        contact_location: form.contact_location,
        whatsapp_number: form.whatsapp_number,
        plan_free_price: Number(form.plan_free_price) || 0,
        plan_premium_price: Number(form.plan_premium_price) || 0,
        plan_vip_price: Number(form.plan_vip_price) || 0,
        wave_number: form.wave_number,
        wave_recipient: form.wave_recipient,
        wave_enabled: !!form.wave_enabled,
        accompagnateurs_visible: !!form.accompagnateurs_visible,
        tarifs_visible: !!form.tarifs_visible,
        primary_accompagnateur_id: form.primary_accompagnateur_id || null,
        logo_url: form.logo_url || null,
        hero_image_url: form.hero_image_url || null,
        how_image_url: form.how_image_url || null,
        tarif_price_afrique: Number(form.tarif_price_afrique) || 0,
        tarif_price_europe: Number(form.tarif_price_europe) || 0,
        tarif_price_usd: Number(form.tarif_price_usd) || 0,
        tarif_price_cad: Number(form.tarif_price_cad) || 0,
        promo_active: !!form.promo_active,
        promo_amount: form.promo_amount || "",
      };
      const { data } = await api.put("/admin/settings", payload);
      setForm((f) => ({ ...f, ...data.settings }));
      refreshSettings();
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSavingSite(false);
    }
  };

  const disableAcc = async (a) => {
    if (!confirm(`Désactiver ${a.name} ? Il/elle sera caché·e des listes publiques.`)) return;
    await api.delete(`/admin/accompagnateurs/${a.id}`);
    toast.success(`${a.name} désactivé`);
    loadAll();
  };

  const createAcc = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/admin/accompagnateurs", {
        name: "Nouvel accompagnateur",
        title: "Écoute & soutien",
        bio: "Présentation à compléter.",
        rate_15: 3000, rate_30: 5500, rate_60: 10000,
        specialties: ["Écoute émotionnelle"],
        languages: ["Français"],
      });
      toast.success("Créé. Modifiez le profil maintenant.");
      setAccs((l) => [...l, data.accompagnateur]);
      setEditing(data.accompagnateur);
    } catch (e) {
      toast.error("Création impossible");
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="container-jamm py-20 text-ink-soft">Chargement…</div>;

  return (
    <div className="container-jamm py-12" data-testid="admin-settings-page">
      <Link to="/admin" className="inline-flex items-center text-ink-soft hover:text-sage-600 text-sm" data-testid="settings-back">
        <ChevronLeft className="w-4 h-4" /> Retour au tableau de bord
      </Link>
      <div className="mt-4 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Super administration</div>
          <h1 className="h-display mt-3">Paramètres du site</h1>
          <p className="lead mt-2">Pilotez Sama Écoute : marque, contact, paiement Wave, affichage et accompagnateurs.</p>
        </div>
        <button
          type="button"
          onClick={async () => {
            try {
              const res = await fetch(`${API}/admin/documentation`, {
                headers: { Authorization: `Bearer ${getToken()}` },
              });
              if (!res.ok) throw new Error("Téléchargement impossible");
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `Sama-Ecoute-Documentation-${new Date().toISOString().slice(0, 10)}.docx`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
              toast.success("Documentation téléchargée");
            } catch (e) {
              toast.error("Téléchargement impossible");
            }
          }}
          className="btn-pill bg-ink text-white px-5 py-2.5 text-sm hover:bg-ink/90"
          data-testid="download-docs-btn"
        >
          <Download className="w-4 h-4" /> Télécharger la documentation
        </button>
      </div>

      <div className="mt-10">
        <MyAccountCard />
      </div>

      <form onSubmit={saveSite} className="grid lg:grid-cols-2 gap-6 mt-6" data-testid="site-settings-form">
        <div className="card-soft lg:col-span-2 bg-gradient-to-br from-sage-50 via-bone to-gold-light/20" data-testid="docs-card">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="grid place-items-center w-12 h-12 rounded-2xl bg-ink text-bone shrink-0">
              <FileText className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-2xl text-ink">Documentation administrateur</h2>
              <p className="text-ink-soft text-sm mt-1">Téléchargez le guide complet (13 sections, Word). Archivez une version pour conserver une trace à chaque changement majeur.</p>
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/admin/documentation`, { headers: { Authorization: `Bearer ${getToken()}` } });
                  if (!res.ok) throw new Error();
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `Sama-Ecoute-Documentation-${new Date().toISOString().slice(0, 10)}.docx`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                  toast.success("Documentation téléchargée");
                } catch (e) {
                  toast.error("Téléchargement impossible");
                }
              }}
              className="btn-pill bg-ink text-white px-5 py-2.5 text-sm hover:bg-ink/90 shrink-0"
              data-testid="download-docs-banner"
            >
              <Download className="w-4 h-4" /> Télécharger (.docx)
            </button>
          </div>

          <div className="mt-6 border-t border-border/60 pt-5">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-gold" />
              <h3 className="font-display text-lg text-ink">Historique des versions</h3>
              <span className="text-xs text-ink-mute" data-testid="doc-versions-count">({docVersions.length})</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <input
                type="text"
                value={docNote}
                onChange={(e) => setDocNote(e.target.value.slice(0, 280))}
                placeholder="Note de version (optionnel) — ex. « Ajout du paiement Orange Money »"
                className="flex-1 rounded-xl border border-border bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sage-300"
                data-testid="doc-version-note"
              />
              <button
                type="button"
                onClick={createDocVersion}
                disabled={creatingDoc}
                className="btn-pill bg-sage-500 text-white px-5 py-2.5 text-sm hover:bg-sage-600 transition disabled:opacity-50 shrink-0"
                data-testid="archive-doc-version-btn"
              >
                <Plus className="w-4 h-4" /> {creatingDoc ? "Archivage…" : "Archiver une nouvelle version"}
              </button>
            </div>

            {docVersions.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border bg-white/60 p-6 text-center text-ink-mute text-sm" data-testid="no-doc-versions">
                Aucune version archivée pour l'instant. Cliquez sur « Archiver » pour sauvegarder une copie datée.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="doc-versions-list">
                {docVersions.map((v) => (
                  <li
                    key={v.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl bg-white border border-border px-4 py-3 hover:border-sage-300 transition"
                    data-testid={`doc-version-${v.label}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sage-50 text-sage-700 text-xs font-semibold">
                          <Clock className="w-3 h-3" /> {v.label}
                        </span>
                        <span className="text-ink text-sm font-medium truncate">{v.filename}</span>
                      </div>
                      <div className="text-xs text-ink-mute mt-1">
                        {new Date(v.created_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}
                        {v.created_by_name ? ` · par ${v.created_by_name}` : ""}
                        {typeof v.size === "number" ? ` · ${(v.size / 1024).toFixed(0)} Ko` : ""}
                      </div>
                      {v.note ? <div className="text-xs text-ink-soft mt-1 italic">« {v.note} »</div> : null}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => downloadDocVersion(v)}
                        className="btn-pill bg-ink text-white px-4 py-2 text-xs hover:bg-ink/90 transition"
                        data-testid={`download-doc-version-${v.label}`}
                      >
                        <Download className="w-3.5 h-3.5" /> Télécharger
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteDocVersion(v)}
                        className="grid place-items-center w-9 h-9 rounded-full text-red-500 hover:bg-red-50 transition"
                        title="Supprimer cette version"
                        data-testid={`delete-doc-version-${v.label}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="card-soft">
          <h2 className="font-display text-2xl text-ink">Identité</h2>
          <p className="text-ink-soft text-sm mt-1">Nom affiché dans le header, le footer, les emails.</p>
          <div className="mt-5 space-y-4">
            <Field icon={Type} label="Nom de la plateforme" value={form.app_name || ""} onChange={(e) => setForm({ ...form, app_name: e.target.value })} data-testid="settings-app-name" />
          </div>
        </div>

        <div className="card-soft">
          <h2 className="font-display text-2xl text-ink">Contact</h2>
          <p className="text-ink-soft text-sm mt-1">Affiché dans le footer et la page contact.</p>
          <div className="mt-5 space-y-4">
            <Field icon={Mail} label="Email de contact" type="email" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="settings-contact-email" />
            <Field icon={Phone} label="Téléphone" value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="settings-contact-phone" />
            <Field icon={MapPin} label="Localisation" value={form.contact_location || ""} onChange={(e) => setForm({ ...form, contact_location: e.target.value })} data-testid="settings-contact-location" />
            <Field icon={MessageCircle} label="WhatsApp (numéro sans + ni espaces)" hint="Exemple : 15142389008" value={form.whatsapp_number || ""} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value.replace(/[^0-9]/g, "") })} data-testid="settings-whatsapp" />
          </div>
        </div>

        <div className="card-soft lg:col-span-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-display text-2xl text-ink flex items-center gap-2"><Smartphone className="w-5 h-5 text-[#0E8DB7]" /> Paiement Wave</h2>
              <p className="text-ink-soft text-sm mt-1">Pour l'instant, seul Wave est accepté. Le numéro Wave et la référence sont affichés à l'utilisateur après réservation.</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-ink-soft">Activer Wave</span>
              <Toggle value={!!form.wave_enabled} onChange={(v) => setForm({ ...form, wave_enabled: v })} testid="settings-wave-enabled" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <Field icon={Smartphone} label="Numéro Wave" hint="Ex : +221 71 011 72 46" value={form.wave_number || ""} onChange={(e) => setForm({ ...form, wave_number: e.target.value })} data-testid="settings-wave-number" />
            <Field icon={Type} label="Destinataire du paiement" hint="Affiché à l'utilisateur" value={form.wave_recipient || ""} onChange={(e) => setForm({ ...form, wave_recipient: e.target.value })} data-testid="settings-wave-recipient" />
          </div>
          <div className="mt-4 rounded-xl bg-beige/60 border border-border p-3 text-xs text-ink-mute leading-relaxed">
            Bientôt : Orange Money, Stripe (carte bancaire), PayPal.
          </div>
        </div>

        <div className="card-soft lg:col-span-2">
          <h2 className="font-display text-2xl text-ink">Affichage des accompagnateurs</h2>
          <p className="text-ink-soft text-sm mt-1">Si désactivé, la liste publique est cachée et la réservation se fait directement avec l'accompagnateur principal.</p>
          <div className="mt-5 flex items-center justify-between rounded-2xl border border-border bg-white p-4">
            <div className="flex items-start gap-3">
              {form.accompagnateurs_visible ? <Eye className="w-5 h-5 text-sage-600 mt-0.5" /> : <EyeOff className="w-5 h-5 text-ink-mute mt-0.5" />}
              <div>
                <div className="font-medium text-ink">Afficher la liste des accompagnateurs</div>
                <div className="text-xs text-ink-mute mt-0.5">Pratique en solo : laissez désactivé. Quand vous aurez des collaborateurs, activez ce toggle.</div>
              </div>
            </div>
            <Toggle value={!!form.accompagnateurs_visible} onChange={(v) => setForm({ ...form, accompagnateurs_visible: v })} testid="settings-acc-visible" />
          </div>

          <div className="mt-5">
            <label className="block text-sm text-ink-soft mb-1.5">Accompagnateur principal (réservation directe)</label>
            <select
              value={form.primary_accompagnateur_id || ""}
              onChange={(e) => setForm({ ...form, primary_accompagnateur_id: e.target.value })}
              className="w-full rounded-xl border border-border bg-white px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-sage-300"
              data-testid="settings-primary-acc"
            >
              <option value="">— Aucun —</option>
              {accs.filter(a => a.active !== false).map((a) => {
                const label = `${a.name} · ${a.title}`;
                return <option key={a.id} value={a.id}>{label}</option>;
              })}
            </select>
          </div>
        </div>

        <div className="card-soft lg:col-span-2 bg-gradient-to-br from-gold-light/20 via-bone to-sage-50">
          <h2 className="font-display text-2xl text-ink flex items-center gap-2">
            <Wallet className="w-5 h-5 text-gold" /> Gestion des Tarifs
          </h2>
          <p className="text-ink-soft text-sm mt-1">Prix Premium par zone géographique et bannière promotionnelle de la page <Link to="/tarifs" className="text-sage-600 underline hover:text-sage-700">/tarifs</Link>.</p>

          {/* Visibilité de la page Tarifs */}
          <div className="mt-5 rounded-2xl border border-border bg-white px-5 py-4 flex items-center justify-between gap-4" data-testid="settings-tarifs-visible-card">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-ink flex items-center gap-2">
                Page Tarifs publique
                {form.tarifs_visible ? (
                  <span className="text-[10px] uppercase tracking-wider text-sage-700 bg-sage-50 border border-sage-200 px-2 py-0.5 rounded-full">visible</span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider text-gold bg-gold/10 border border-gold/30 px-2 py-0.5 rounded-full">masquée</span>
                )}
              </div>
              <div className="text-xs text-ink-mute mt-1">
                {form.tarifs_visible
                  ? "Les visiteurs et clients voient la page /tarifs et le lien dans la navigation."
                  : "Le lien Tarifs est masqué pour les visiteurs et clients. Vous (admin) gardez l'accès en aperçu."}
              </div>
            </div>
            <Toggle
              value={!!form.tarifs_visible}
              onChange={(v) => setForm({ ...form, tarifs_visible: v })}
              testid="settings-tarifs-visible"
            />
          </div>

          {/* Prix Premium par zone */}
          <div className="mt-5">
            <div className="overline text-sage-600 mb-2">Prix Premium par zone</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm text-ink-soft mb-1.5">🌍 Sénégal & Afrique <span className="text-ink-mute">(F CFA)</span></label>
                <input
                  type="number" min="0" step="100"
                  value={form.tarif_price_afrique}
                  onChange={(e) => setForm({ ...form, tarif_price_afrique: e.target.value })}
                  className="w-full rounded-xl border border-border bg-white px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-sage-300"
                  data-testid="settings-tarif-afrique"
                />
              </div>
              <div>
                <label className="block text-sm text-ink-soft mb-1.5">🇪🇺 Europe <span className="text-ink-mute">(€)</span></label>
                <input
                  type="number" min="0" step="1"
                  value={form.tarif_price_europe}
                  onChange={(e) => setForm({ ...form, tarif_price_europe: e.target.value })}
                  className="w-full rounded-xl border border-border bg-white px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-sage-300"
                  data-testid="settings-tarif-europe"
                />
              </div>
              <div>
                <label className="block text-sm text-ink-soft mb-1.5">🌎 Amérique <span className="text-ink-mute">($ USD)</span></label>
                <input
                  type="number" min="0" step="1"
                  value={form.tarif_price_usd}
                  onChange={(e) => setForm({ ...form, tarif_price_usd: e.target.value })}
                  className="w-full rounded-xl border border-border bg-white px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-sage-300"
                  data-testid="settings-tarif-usd"
                />
              </div>
              <div>
                <label className="block text-sm text-ink-soft mb-1.5">🌎 Amérique <span className="text-ink-mute">($ CAD)</span></label>
                <input
                  type="number" min="0" step="1"
                  value={form.tarif_price_cad}
                  onChange={(e) => setForm({ ...form, tarif_price_cad: e.target.value })}
                  className="w-full rounded-xl border border-border bg-white px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-sage-300"
                  data-testid="settings-tarif-cad"
                />
              </div>
            </div>
          </div>

          {/* Bannière promotionnelle */}
          <div className="mt-7 pt-5 border-t border-border/60">
            <div className="overline text-gold mb-2">Bannière promotionnelle</div>
            <div className="grid sm:grid-cols-3 gap-4 items-start">
              <div className="flex items-center justify-between rounded-xl border border-border bg-white px-4 py-3">
                <div>
                  <div className="text-sm font-medium text-ink">Afficher</div>
                  <div className="text-xs text-ink-mute">ON / OFF</div>
                </div>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, promo_active: !form.promo_active })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${form.promo_active ? "bg-sage-500" : "bg-ink-mute/40"}`}
                  data-testid="settings-promo-active"
                  aria-pressed={form.promo_active}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${form.promo_active ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm text-ink-soft mb-1.5">Montant promotionnel <span className="text-ink-mute">(texte libre)</span></label>
                <input
                  type="text"
                  value={form.promo_amount}
                  placeholder="Ex. : 5 000 FCFA  ou  À partir de 9€"
                  onChange={(e) => setForm({ ...form, promo_amount: e.target.value.slice(0, 80) })}
                  className="w-full rounded-xl border border-border bg-white px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-sage-300"
                  data-testid="settings-promo-amount"
                />
                <p className="mt-1.5 text-xs text-ink-mute">Affiché tel quel dans la bannière : « 🎉 Tarif promotionnel : <strong>{form.promo_amount || "[montant]"}</strong> — Offre limitée ! »</p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={saveSite}
            disabled={savingSite}
            className="btn-primary mt-6"
            data-testid="settings-save-tarifs"
          >
            <Save className="w-4 h-4" /> {savingSite ? "Enregistrement…" : "Enregistrer les tarifs"}
          </button>
        </div>

        <div className="card-soft lg:col-span-2">
          <h2 className="font-display text-2xl text-ink flex items-center gap-2"><ImageIcon className="w-5 h-5 text-sage-600" /> Images de la plateforme</h2>
          <p className="text-ink-soft text-sm mt-1">Personnalisez le logo et les visuels de la page d'accueil. Stockage sécurisé Emergent.</p>
          <div className="grid md:grid-cols-3 gap-4 mt-5">
            <ImageUploader label="Logo (carré)" aspect="1/1" value={form.logo_url} onChange={(url) => setForm({ ...form, logo_url: url })} testid="uploader-logo" />
            <ImageUploader label="Image hero (accueil)" aspect="4/5" value={form.hero_image_url} onChange={(url) => setForm({ ...form, hero_image_url: url })} testid="uploader-hero" />
            <ImageUploader label="Image 'Espace'" aspect="4/5" value={form.how_image_url} onChange={(url) => setForm({ ...form, how_image_url: url })} testid="uploader-how" />
          </div>
          <div className="mt-3 text-xs text-ink-mute">Laissez vide pour utiliser les visuels par défaut.</div>
        </div>


        <div className="lg:col-span-2 flex justify-end">
          <button type="submit" disabled={savingSite} className="btn-primary" data-testid="settings-save-site">
            <Save className="w-4 h-4" /> {savingSite ? "Enregistrement…" : "Enregistrer les paramètres"}
          </button>
        </div>
      </form>

      <section className="mt-14">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-2xl text-ink">Accompagnateurs</h2>
            <p className="text-ink-soft text-sm mt-1">Créez, modifiez ou désactivez les profils. Inclut Cathy Bal.</p>
          </div>
          <button onClick={createAcc} disabled={creating} className="btn-primary text-sm" data-testid="acc-create">
            <Plus className="w-4 h-4" /> Nouvel accompagnateur
          </button>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {accs.map((a) => (
            <div key={a.id} className={`card-soft ${a.active === false ? "opacity-60" : ""}`} data-testid={`settings-acc-card-${a.id}`}>
              <div className="flex items-center gap-3">
                <img src={a.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-sage-100" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg text-ink truncate flex items-center gap-2">
                    {a.name}
                    {a.active === false && <span className="text-[10px] uppercase tracking-wider text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">inactif</span>}
                  </div>
                  <div className="text-xs text-ink-mute truncate">{a.title}</div>
                </div>
              </div>
              <div className="mt-3 text-xs text-ink-soft">
                {formatXOF(a.rate_15)} · {formatXOF(a.rate_30)} · {formatXOF(a.rate_60)}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {(a.specialties || []).slice(0, 3).map((s) => <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-sage-50 text-sage-700 border border-sage-100">{s}</span>)}
              </div>
              <div className="mt-4 flex items-center justify-between">
                <button onClick={() => setEditing(a)} className="btn-outline !py-1.5 !px-4 text-xs" data-testid={`acc-edit-${a.id}`}>Modifier</button>
                {a.active !== false && (
                  <button onClick={() => disableAcc(a)} className="btn-pill text-red-600 hover:text-red-700 px-3 py-1.5 text-xs" data-testid={`acc-disable-${a.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Désactiver
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {editing && (
        <AccompagnateurEditor
          acc={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setAccs((list) => list.map((x) => (x.id === updated.id ? updated : x)));
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
